import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDashboardPath, getSafeReturnPath } from '../../lib/authRouting';
import api, { getApiErrorMessage } from '../../services/api';
import {
  initializeMsg91Widget,
  isMsg91WidgetProvider,
  retryMsg91WidgetOtp,
  sendMsg91WidgetOtp,
  verifyMsg91WidgetOtp,
} from '../../services/msg91OtpWidget';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  CalendarCheck,
  Camera,
  CaretLeft,
  CaretRight,
  Eye,
  EyeSlash,
  LockKey,
  Phone,
  UsersThree,
  WhatsappLogo,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const FEATURE_SLIDES = [
  {
    icon: UsersThree,
    title: 'Guests, beautifully organized',
    description: 'Manage guest lists, groups, plus-ones and RSVPs from one calm workspace.',
    artworkClass: 'from-emerald-100 via-teal-50 to-white text-emerald-700',
    accentClass: 'bg-emerald-600',
  },
  {
    icon: CalendarCheck,
    title: 'Every moment, perfectly timed',
    description: 'Build itineraries that keep hosts, teams and guests on the same page.',
    artworkClass: 'from-amber-100 via-orange-50 to-white text-amber-700',
    accentClass: 'bg-amber-500',
  },
  {
    icon: WhatsappLogo,
    title: 'Communication without chaos',
    description: 'Send invitations and important event updates through WhatsApp with ease.',
    artworkClass: 'from-green-100 via-emerald-50 to-white text-green-700',
    accentClass: 'bg-green-600',
  },
  {
    icon: Camera,
    title: 'Memories in one shared place',
    description: 'Collect, organize and share celebration photos with everyone who matters.',
    artworkClass: 'from-rose-100 via-pink-50 to-white text-rose-700',
    accentClass: 'bg-rose-500',
  },
];

/**
 * Phone-first authentication page.
 *
 * The user enters a phone number first. OTP is the default for both login and
 * sign-up; existing users can explicitly choose password login as a fallback.
 *
 * When the user arrives via a WhatsApp invite URL we pre-fill and lock the
 * invite code and phone number.
 */
export default function AuthPage() {
  const [authStep, setAuthStep] = useState('phone');
  const [featureSlide, setFeatureSlide] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Config from backend – tells us whether OTP is available at all.
  const [otpConfig, setOtpConfig] = useState(null);
  const otpEnabled = Boolean(otpConfig?.otp_enabled);
  const otpLength = Number(otpConfig?.otp_length) || 6;

  const { login, loginWithOtp, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ---------- URL pre-fill (from WhatsApp invite) ---------------------------
  const inviteToken = (searchParams.get('invite_token') || searchParams.get('t') || '').trim();
  const inviteEventId = (searchParams.get('event_id') || '').trim();
  const returnTo = useMemo(
    () => getSafeReturnPath(searchParams.get('return_to')),
    [searchParams],
  );
  const prefillCode = (searchParams.get('code') || '').trim();
  const prefillPhoneRaw = (searchParams.get('phone') || '').trim();
  const prefillPhone = useMemo(() => {
    if (!prefillPhoneRaw) return '';
    // The URL uses `+` as a literal – React-Router already decodes plus signs
    // to spaces when reading query params, so restore them.
    if (prefillPhoneRaw.startsWith(' ')) return `+${prefillPhoneRaw.slice(1)}`;
    return prefillPhoneRaw;
  }, [prefillPhoneRaw]);
  const hasInviteContext = Boolean(inviteToken || prefillCode || prefillPhone);

  // ---------- Form state ----------------------------------------------------
  const [loginData, setLoginData] = useState({ phone: '', password: '' });

  const [registerData, setRegisterData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    inviteCode: '',
  });

  // OTP dialog state (shared by "login with OTP", "forgot password" and "register verify")
  const [otpDialog, setOtpDialog] = useState({
    open: false,
    mode: null, // 'login' | 'reset' | 'register'
    phone: '',
    code: '',
    otpSent: false,
    otpVerifiedToken: '',
    resetPassword: '',
    resetPasswordConfirm: '',
    devCode: '', // present when server returns a dev/mock OTP
    requestId: '', // MSG91 Widget request identifier
  });
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  // ---------- Effects -------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/auth/config');
        if (!cancelled) setOtpConfig(res.data);
      } catch (err) {
        console.warn('Failed to fetch auth config', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!otpDialog.open || resendSeconds <= 0) return undefined;
    const timer = window.setTimeout(() => {
      setResendSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [otpDialog.open, resendSeconds]);

  useEffect(() => {
    if (prefillCode || prefillPhone) {
      setLoginData((prev) => ({ ...prev, phone: prefillPhone || prev.phone }));
      setRegisterData((prev) => ({
        ...prev,
        inviteCode: prefillCode ? prefillCode.toUpperCase() : prev.inviteCode,
        phone: prefillPhone || prev.phone,
      }));
    }
  }, [prefillCode, prefillPhone]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setFeatureSlide((current) => (current + 1) % FEATURE_SLIDES.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, []);

  // ---------- Handlers ------------------------------------------------------
  const routeAfterAuth = (userRole) => {
    navigate(returnTo || getDashboardPath(userRole));
  };

  const handlePhoneContinue = async (event, method = 'otp') => {
    event.preventDefault();
    const phone = loginData.phone.trim();
    if (!phone) {
      toast.error('Phone number is required');
      return;
    }

    setLoginData((prev) => ({ ...prev, phone, password: '' }));
    setRegisterData((prev) => ({ ...prev, phone, password: '' }));
    setShowPassword(false);
    if (method === 'password') {
      setLoading(true);
      try {
        const response = await api.get('/auth/phone-status', { params: { phone } });
        if (response.data?.registered) {
          setAuthStep('login');
        } else {
          setAuthStep('register');
          toast.info('No account was found. Let’s create one.');
        }
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Could not check this phone number. Please try again.'));
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      let config = otpConfig;
      if (!config) {
        try {
          const response = await api.get('/auth/config');
          config = response.data;
          setOtpConfig(config);
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Could not load OTP settings. Please try again.'));
          return;
        }
      }
      if (!config?.otp_enabled) {
        toast.error('OTP is disabled on the server. Please use password login.');
        return;
      }

      openOtpDialog('login', phone, config);
      const result = await requestOtp('login', phone, { suppressNoAccount: true, config });
      if (result === 'no-account') {
        setOtpDialog((prev) => ({ ...prev, open: false }));
        setAuthStep('register');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetPhoneStep = () => {
    setAuthStep('phone');
    setShowPassword(false);
    setLoginData((prev) => ({ ...prev, password: '' }));
    setRegisterData((prev) => ({ ...prev, name: '', password: '' }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginData.phone.trim(), loginData.password, {
        inviteToken,
        eventId: inviteEventId,
      });
      toast.success('Welcome back!');
      routeAfterAuth(user.effective_role || user.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!registerData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    // If OTP is enabled, we must verify the phone first.
    if (otpEnabled) {
      const phone = registerData.phone.trim();
      openOtpDialog('register', phone);
      await requestOtp('register', phone);
      return;
    }

    setLoading(true);
    try {
      const user = await register({
        name: registerData.name,
        phone: registerData.phone.trim(),
        email: registerData.email.trim() || null,
        password: registerData.password,
        inviteCode: registerData.inviteCode || null,
        inviteToken: inviteToken || null,
        eventId: inviteEventId || null,
        otpVerifiedToken: null,
      });
      toast.success('Account created successfully!');
      routeAfterAuth(user.effective_role || user.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // ---------- OTP dialog ----------------------------------------------------
  const openOtpDialog = (mode, phone, config = otpConfig) => {
    if (!config?.otp_enabled) {
      toast.error('OTP verification is not configured on the server yet. Please use password login.');
      return;
    }
    if (!phone) {
      toast.error('Please enter your phone number first');
      return;
    }
    setResendSeconds(0);
    setOtpDialog({
      open: true,
      mode,
      phone,
      code: '',
      otpSent: false,
      otpVerifiedToken: '',
      resetPassword: '',
      resetPasswordConfirm: '',
      devCode: '',
      requestId: '',
    });
  };

  const purposeFromMode = (mode) => {
    if (mode === 'login') return 'login';
    if (mode === 'reset') return 'reset_password';
    return 'register';
  };

  const requestOtp = async (mode, phone, { suppressNoAccount = false, config = otpConfig } = {}) => {
    setOtpLoading(true);
    try {
      if (isMsg91WidgetProvider(config)) {
        await initializeMsg91Widget(config);
      }
      const res = await api.post('/auth/otp/send', {
        phone,
        purpose: purposeFromMode(mode),
      });
      let requestId = '';
      if (res.data?.client_send_required && isMsg91WidgetProvider(config)) {
        const widgetResult = await sendMsg91WidgetOtp(config, res.data.phone || phone);
        requestId = widgetResult.requestId;
      }
      setOtpDialog((prev) => ({
        ...prev,
        otpSent: true,
        devCode: res.data?.dev_code || '',
        requestId,
      }));
      setResendSeconds(30);
      if (res.data?.dev_code) {
        toast.info(`Dev OTP: ${res.data.dev_code} (mock provider)`);
      } else if (isMsg91WidgetProvider(config)) {
        toast.success(`OTP sent on WhatsApp to ${phone}`);
      } else {
        toast.success(`OTP sent to ${phone}`);
      }
    } catch (error) {
      if (suppressNoAccount && error.response?.status === 404
        && error.response?.data?.detail === 'No account found with this phone number') {
        return 'no-account';
      }
      toast.error(getApiErrorMessage(error, error.message || 'Failed to send OTP'));
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSendOtp = () => requestOtp(otpDialog.mode, otpDialog.phone);

  const handleResendOtp = async () => {
    if (!isMsg91WidgetProvider(otpConfig)) {
      await handleSendOtp();
      return;
    }
    setOtpLoading(true);
    try {
      await retryMsg91WidgetOtp(otpConfig, otpDialog.requestId);
      setResendSeconds(30);
      toast.success(`OTP resent on WhatsApp to ${otpDialog.phone}`);
    } catch (error) {
      toast.error(error.message || 'Failed to resend OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (codeOverride) => {
    const code = typeof codeOverride === 'string' ? codeOverride.trim() : otpDialog.code.trim();
    if (!code) {
      toast.error('Please enter the OTP');
      return;
    }
    if (code.length !== otpLength) {
      toast.error(`Please enter the complete ${otpLength}-digit OTP`);
      return;
    }
    setOtpLoading(true);
    try {
      const purpose = purposeFromMode(otpDialog.mode);
      let otpAccessToken = null;
      if (isMsg91WidgetProvider(otpConfig)) {
        const widgetResult = await verifyMsg91WidgetOtp(
          otpConfig,
          code,
          otpDialog.requestId,
        );
        otpAccessToken = widgetResult.accessToken;
      }

      if (otpDialog.mode === 'login') {
        const user = await loginWithOtp(otpDialog.phone, code, {
          inviteToken,
          eventId: inviteEventId,
          otpAccessToken,
        });
        toast.success('Welcome back!');
        setOtpDialog((prev) => ({ ...prev, open: false }));
        routeAfterAuth(user.effective_role || user.role);
        return;
      }

      // For 'register' and 'reset' we call /auth/otp/verify -> get token.
      const res = await api.post('/auth/otp/verify', {
        phone: otpDialog.phone,
        otp_code: code,
        otp_access_token: otpAccessToken,
        purpose,
      });
      const token = res.data?.otp_verified_token;
      setOtpDialog((prev) => ({ ...prev, otpVerifiedToken: token }));

      if (otpDialog.mode === 'register') {
        // Now finish registration with the verified token.
        const user = await register({
          name: registerData.name,
          phone: otpDialog.phone,
          email: registerData.email.trim() || null,
          password: null,
          inviteCode: registerData.inviteCode || null,
          inviteToken: inviteToken || null,
          eventId: inviteEventId || null,
          otpVerifiedToken: token,
        });
        toast.success('Account created successfully!');
        setOtpDialog((prev) => ({ ...prev, open: false }));
        routeAfterAuth(user.effective_role || user.role);
      } else {
        // Reset flow — user still needs to enter the new password.
        toast.success('OTP verified. Please enter a new password.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'OTP verification failed');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpCodeChange = (event) => {
    const code = event.target.value.replace(/\D/g, '').slice(0, otpLength);
    setOtpDialog((prev) => ({ ...prev, code }));
    if (code.length === otpLength && !otpLoading) handleVerifyOtp(code);
  };

  const switchOtpDialogToPassword = () => {
    setOtpDialog((prev) => ({ ...prev, open: false }));
    setAuthMethod('password');
    setAuthStep('login');
    setLoginData((prev) => ({ ...prev, phone: otpDialog.phone, password: '' }));
  };

  const editOtpPhone = () => {
    setOtpDialog((prev) => ({ ...prev, open: false }));
    resetPhoneStep();
  };

  const handleResetPassword = async () => {
    if (!otpDialog.resetPassword || otpDialog.resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (otpDialog.resetPassword !== otpDialog.resetPasswordConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    setOtpLoading(true);
    try {
      await api.post('/auth/reset-password-otp', {
        phone: otpDialog.phone,
        otp_verified_token: otpDialog.otpVerifiedToken,
        new_password: otpDialog.resetPassword,
      });
      toast.success('Password updated. Please sign in with your new password.');
      setOtpDialog((prev) => ({ ...prev, open: false }));
      setAuthStep('login');
      setLoginData((prev) => ({ ...prev, phone: otpDialog.phone, password: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setOtpLoading(false);
    }
  };

  const showPreviousFeature = () => {
    setFeatureSlide((current) => (current - 1 + FEATURE_SLIDES.length) % FEATURE_SLIDES.length);
  };

  const showNextFeature = () => {
    setFeatureSlide((current) => (current + 1) % FEATURE_SLIDES.length);
  };

  const activeFeature = FEATURE_SLIDES[featureSlide];
  const ActiveFeatureIcon = activeFeature.icon;

  // ---------- Render --------------------------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-b from-wedding-cream to-wedding-sage-light flex items-center justify-center px-4 py-6 sm:py-8">
      <div className="w-full max-w-md">
        <div className="mb-5 animate-fade-in">
          <div className="flex items-center justify-center gap-3">
            <img src="/assets/logo.png" alt="GalaGo logo" className="h-14 w-14 object-contain" />
            <h1
              className="text-4xl font-bold leading-none tracking-[-0.035em] text-wedding-charcoal"
              style={{ fontFamily: "'Manrope', ui-sans-serif, system-ui, sans-serif" }}
            >
              GalaGo
            </h1>
          </div>
          <p className="mt-2 text-center text-sm text-muted-foreground">Events of every kind, perfectly managed</p>
        </div>

        <Card className="overflow-hidden border-0 animate-fade-in stagger-1">
          <section
            className="relative border-b border-wedding-sage/10 bg-gradient-to-b from-white/80 to-wedding-sage-light/20 px-5 pb-4 pt-5"
            aria-roledescription="carousel"
            aria-label="GalaGo feature highlights"
            data-testid="feature-carousel"
          >
            <div className="min-h-[160px] text-center" aria-live="polite">
              <div className={`relative mx-auto grid h-16 w-16 place-items-center overflow-hidden rounded-2xl bg-gradient-to-br shadow-sm ${activeFeature.artworkClass}`}>
                <span className="absolute -right-3 -top-3 h-10 w-10 rounded-full bg-white/60" />
                <span className="absolute -bottom-4 -left-3 h-9 w-9 rounded-full bg-white/50" />
                <ActiveFeatureIcon className="relative h-9 w-9" weight="duotone" />
              </div>
              <h2 className="mt-3 text-base font-semibold text-wedding-charcoal">{activeFeature.title}</h2>
              <p className="mx-auto mt-1 max-w-sm text-xs leading-5 text-muted-foreground">
                {activeFeature.description}
              </p>
            </div>

            <div className="mt-1 flex items-center justify-between">
              <button
                type="button"
                onClick={showPreviousFeature}
                className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-white hover:text-wedding-charcoal"
                aria-label="Previous feature"
              >
                <CaretLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-1.5" aria-hidden="true" data-testid="feature-carousel-indicators">
                {FEATURE_SLIDES.map((slide, index) => (
                  <span
                    key={slide.title}
                    className={`h-1.5 rounded-full transition-all ${index === featureSlide ? `w-5 ${slide.accentClass}` : 'w-1.5 bg-wedding-charcoal/20'}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={showNextFeature}
                className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-white hover:text-wedding-charcoal"
                aria-label="Next feature"
              >
                <CaretRight className="h-4 w-4" />
              </button>
            </div>
          </section>

          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-serif">Login or Sign Up</CardTitle>
            {/* <CardDescription>
              {authStep === 'phone' && (hasInviteContext
                ? 'Continue with the phone number from your invitation'
                : 'Enter your phone number to continue')}
              {authStep === 'login' && 'Enter your password to continue'}
              {authStep === 'register' && 'Tell us your name and create a password'}
            </CardDescription> */}
          </CardHeader>
          <CardContent>
            {authStep === 'phone' && (
              <form onSubmit={handlePhoneContinue} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-phone">Phone Number</Label>
                  <Input
                    id="login-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    autoFocus={!prefillPhone}
                    placeholder="+91 98765 43210"
                    data-testid="login-phone"
                    value={loginData.phone}
                    onChange={(event) => setLoginData((prev) => ({ ...prev, phone: event.target.value }))}
                    readOnly={Boolean(prefillPhone)}
                    className={prefillPhone ? 'cursor-not-allowed bg-muted/50' : ''}
                    required
                  />
                  {prefillPhone && (
                    <p className="text-xs text-muted-foreground">
                      This phone number was included in your invitation link.
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-wedding-sage hover:bg-wedding-sage/90"
                  disabled={loading}
                  data-testid="phone-continue"
                >
                  {loading ? 'Checking...' : 'Continue with WhatsApp OTP'}
                </Button>
                <button
                  type="button"
                  className="block w-full text-center text-xs text-muted-foreground hover:text-wedding-sage hover:underline"
                  onClick={(event) => handlePhoneContinue(event, 'password')}
                  disabled={loading}
                  data-testid="auth-method-toggle"
                >
                  Login using password
                </button>
              </form>
            )}

            {authStep === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between rounded-lg bg-muted/45 px-3 py-2.5">
                    <div>
                      <p className="text-[0.6875rem] text-muted-foreground">Phone number</p>
                      <p className="text-sm font-medium" data-testid="login-phone-confirmed">{loginData.phone}</p>
                    </div>
                    {!prefillPhone && (
                      <button type="button" className="text-xs font-medium text-wedding-sage hover:underline" onClick={resetPhoneStep}>
                        Change
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    {otpEnabled && (
                      <button
                        type="button"
                        className="text-xs text-wedding-sage hover:underline"
                        onClick={async () => {
                          const phone = loginData.phone.trim();
                          openOtpDialog('reset', phone);
                          await requestOtp('reset', phone);
                        }}
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      autoFocus
                      placeholder="Enter your password"
                      data-testid="login-password"
                      value={loginData.password}
                      onChange={(event) => setLoginData((prev) => ({ ...prev, password: event.target.value }))}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-wedding-charcoal"
                      onClick={() => setShowPassword((visible) => !visible)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeSlash className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-wedding-sage hover:bg-wedding-sage/90"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Signing in...' : 'Login'}
                </Button>
                {otpEnabled && (
                  <button
                    type="button"
                    className="block w-full text-center text-xs text-muted-foreground hover:text-wedding-sage hover:underline"
                    onClick={async () => {
                      const phone = loginData.phone.trim();
                      openOtpDialog('login', phone);
                      await requestOtp('login', phone);
                    }}
                    data-testid="login-with-otp-link"
                  >
                    Login using OTP instead
                  </button>
                )}
                {!otpEnabled && (
                  <button
                    type="button"
                    className="block w-full text-center text-xs text-muted-foreground hover:text-wedding-sage hover:underline"
                    onClick={() => {
                      setRegisterData((prev) => ({ ...prev, phone: loginData.phone.trim() }));
                      setAuthStep('register');
                    }}
                  >
                    Create an account
                  </button>
                )}
              </form>
            )}

            {authStep === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="flex items-center justify-between rounded-lg bg-muted/45 px-3 py-2.5">
                  <div>
                    <p className="text-[0.6875rem] text-muted-foreground">Phone number</p>
                    <p className="text-sm font-medium text-wedding-charcoal">{registerData.phone}</p>
                  </div>
                  {!prefillPhone && (
                    <button type="button" className="text-xs font-medium text-wedding-sage hover:underline" onClick={resetPhoneStep}>
                      Change
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-name">Full Name</Label>
                  <Input
                    id="register-name"
                    type="text"
                    autoComplete="name"
                    autoFocus
                    placeholder="Your name"
                    data-testid="register-name"
                    value={registerData.name}
                    onChange={(event) => setRegisterData((prev) => ({ ...prev, name: event.target.value }))}
                    required
                  />
                </div>
                {!otpEnabled && (
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Create Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="At least 6 characters"
                      data-testid="register-password"
                      value={registerData.password}
                      onChange={(event) => setRegisterData((prev) => ({ ...prev, password: event.target.value }))}
                      required
                      minLength={6}
                    />
                  </div>
                )}
                {prefillCode && (
                  <p className="rounded-md bg-muted/45 px-3 py-2 text-xs text-muted-foreground">
                    Your invitation code has been applied automatically.
                  </p>
                )}
                <Button
                  type="submit"
                  className="w-full bg-wedding-sage hover:bg-wedding-sage/90"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading
                    ? 'Creating account...'
                    : otpEnabled
                      ? 'Send WhatsApp OTP'
                      : 'Create Account'}
                </Button>
                {otpEnabled && (
                  <p className="text-center text-[0.6875rem] text-muted-foreground">
                    You can create an optional password later in Settings.
                  </p>
                )}
              </form>
            )}

          </CardContent>
        </Card>
      </div>

      {/* ------------------- OTP DIALOG ------------------- */}
      <Dialog
        open={otpDialog.open}
        onOpenChange={(open) => {
          setOtpDialog((prev) => ({ ...prev, open }));
          if (!open) {
            setOtpLoading(false);
            setResendSeconds(0);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              {otpDialog.mode === 'login' && <><Phone className="w-5 h-5" /> Login with OTP</>}
              {otpDialog.mode === 'register' && <><Phone className="w-5 h-5" /> Verify your phone</>}
              {otpDialog.mode === 'reset' && <><LockKey className="w-5 h-5" /> Reset password</>}
            </DialogTitle>
            <DialogDescription>
              {otpDialog.otpSent
                ? `Enter the ${otpLength}-digit code sent${isMsg91WidgetProvider(otpConfig) ? ' on WhatsApp' : ''} to ${otpDialog.phone}`
                : `We'll send a one-time password${isMsg91WidgetProvider(otpConfig) ? ' on WhatsApp' : ''} to ${otpDialog.phone}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {!otpDialog.otpSent && (
              <div className="space-y-3 text-center">
                <p className="text-sm text-muted-foreground">
                  {otpLoading ? 'Sending your WhatsApp OTP…' : 'We could not send the OTP. Please try again.'}
                </p>
                {!otpLoading && (
                <Button
                  type="button"
                  className="w-full bg-wedding-sage hover:bg-wedding-sage/90"
                  onClick={handleSendOtp}
                  data-testid="retry-send-otp-btn"
                >
                  Try again
                </Button>
                )}
              </div>
            )}

            {otpDialog.otpSent && !otpDialog.otpVerifiedToken && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="otp-code">OTP Code</Label>
                  <Input
                    id="otp-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={'0'.repeat(otpLength)}
                    value={otpDialog.code}
                    onChange={handleOtpCodeChange}
                    maxLength={otpLength}
                    autoFocus
                    aria-describedby="otp-auto-verify-help"
                    data-testid="otp-code-input"
                  />
                  <p id="otp-auto-verify-help" className="text-xs text-muted-foreground">
                    Verification starts automatically after all {otpLength} digits are entered.
                  </p>
                  {otpDialog.devCode && (
                    <p className="text-xs text-orange-600">
                      Dev mode – OTP: <code>{otpDialog.devCode}</code>
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleResendOtp}
                    disabled={otpLoading || resendSeconds > 0}
                    data-testid="resend-otp-btn"
                  >
                    {resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend OTP'}
                  </Button>
                  {otpLoading && otpDialog.code.length === otpLength && (
                    <p className="text-center text-sm text-muted-foreground">Verifying…</p>
                  )}
                </div>
              </>
            )}

            {otpDialog.mode === 'reset' && otpDialog.otpVerifiedToken && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="reset-password">New Password</Label>
                  <Input
                    id="reset-password"
                    type={showPassword ? 'text' : 'password'}
                    value={otpDialog.resetPassword}
                    onChange={(e) => setOtpDialog({ ...otpDialog, resetPassword: e.target.value })}
                    minLength={6}
                    data-testid="reset-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-password-confirm">Confirm New Password</Label>
                  <Input
                    id="reset-password-confirm"
                    type={showPassword ? 'text' : 'password'}
                    value={otpDialog.resetPasswordConfirm}
                    onChange={(e) => setOtpDialog({ ...otpDialog, resetPasswordConfirm: e.target.value })}
                    minLength={6}
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <Button
                  type="button"
                  className="w-full bg-wedding-sage hover:bg-wedding-sage/90"
                  onClick={handleResetPassword}
                  disabled={otpLoading}
                  data-testid="reset-password-submit"
                >
                  {otpLoading ? 'Updating…' : 'Update Password'}
                </Button>
              </>
            )}
          </div>

          <DialogFooter>
            {!prefillPhone && (
              <Button type="button" variant="ghost" onClick={editOtpPhone}>
                Edit phone
              </Button>
            )}
            {otpDialog.mode === 'login' && (
              <Button type="button" variant="ghost" onClick={switchOtpDialogToPassword}>
                Use password
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOtpDialog((prev) => ({ ...prev, open: false }))}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
